import React from "react";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import GoogleSignInButton from "@/components/shared/ui/GoogleSignInButton";
import NeutralOrDarkProfileSVG from "./ProfileSVG";
import Box from "@mui/material/Box";
import { User } from "@/types/user";
import { useT } from "@/i18n/useT";
import { useColorScheme } from "@mui/material/styles";
import { usePathname } from "next/navigation";
import LogoutIcon from "@mui/icons-material/Logout";
import PaletteIcon from "@mui/icons-material/Palette";
import LanguageIcon from "@mui/icons-material/Language";
import SettingsIcon from "@mui/icons-material/Settings";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  AUTO_PLAY_KEY,
  JUMP_TO_LAST_WATCH_ENABLE_KEY,
  LOCALS,
} from "@/consts/settings";
import { FormControlLabel, Switch } from "@mui/material";
import { useVideoPlayer } from "@/lib/useVideoPlayer";
import { useNavigation } from "@/lib/navigation";

interface Props {
  user: User | null;
  loading: boolean;
  anchorEl: HTMLElement | null;
  menuId: string;
  handleProfileMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  handleMenuClose: () => void;
  t: ReturnType<typeof useT>;
  logout: () => void;
  loginWithGoogle: () => void;
  compact?: boolean;
}

type SubmenuType = "theme" | "language" | "settings" | null;

export default function UserProfileOrCTAButtons({
  user,
  loading,
  anchorEl,
  menuId,
  handleProfileMenuOpen,
  handleMenuClose,
  t,
  logout,
  loginWithGoogle,
  compact = false,
}: Props) {
  const [activeSubmenu, setActiveSubmenu] = React.useState<SubmenuType>(null);
  const { autoPlay, jumpToLastWatchEnable, changeSetting } = useVideoPlayer();
  const { mode, setMode } = useColorScheme();
  const pathname = usePathname();
  const tTheme = useT("ui.theme"); // Add theme translation context
  const tSettings = useT("settings");
  const navigation = useNavigation();

  // Detect current locale from pathname
  const currentLocale =
    LOCALS.find((l) => pathname.startsWith(`/${l.code}`))?.code || "en";

  const handleSubmenuOpen = (submenu: SubmenuType) => {
    setActiveSubmenu(submenu);
  };

  const handleSubmenuBack = () => {
    setActiveSubmenu(null);
  };

  const handleMenuCloseWrapper = () => {
    setActiveSubmenu(null);
    handleMenuClose();
  };

  const handleThemeChange = (newMode: "light" | "dark" | "system") => {
    setMode(newMode);
    handleMenuCloseWrapper();
  };

  const handleLanguageChange = (newLocale: string) => {
    navigation.switchLanguage(
      newLocale,
      pathname,
      LOCALS.map((l) => l.code),
      { locale: newLocale, reason: "User language selection" }
    );
    handleMenuCloseWrapper();
  };

  const getThemeLabel = (themeMode: typeof mode) => {
    switch (themeMode) {
      case "system":
        return tTheme("system");
      case "light":
        return tTheme("light");
      case "dark":
        return tTheme("dark");
      default:
        return tTheme("system");
    }
  };

  const getCurrentLanguageLabel = () => {
    return LOCALS.find((l) => l.code === currentLocale)?.label || "English";
  };

  if (loading) return null;
  if (user) {
    if (compact) {
      return (
        <IconButton
          size="large"
          aria-label="account of current user"
          color="inherit"
          sx={{ p: 0 }}
        >
          <Avatar
            src={user.photoURL || undefined}
            alt={user.displayName || "User"}
            sx={{
              width: 32,
              height: 32,
              transition: "width 0.3s, height 0.3s",
            }}
          />
        </IconButton>
      );
    }
    // Not compact: center horizontally
    return (
      <Box
        sx={{
          width: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <IconButton
          size="large"
          edge="end"
          aria-label="account of current user"
          aria-controls={menuId}
          aria-haspopup="true"
          onClick={handleProfileMenuOpen}
          color="inherit"
          sx={{ p: 0 }}
        >
          <Avatar
            src={user.photoURL || undefined}
            alt={user.displayName || "User"}
            sx={{
              width: 32,
              height: 32,
              transition: "width 0.3s, height 0.3s",
            }}
          />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          id={menuId}
          keepMounted
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          open={Boolean(anchorEl)}
          onClose={handleMenuCloseWrapper}
          sx={{
            zIndex: 1302, // Ensure menu appears above header
            "& .MuiPaper-root": {
              minWidth: 240,
            },
          }}
        >
          {activeSubmenu === null && [
            /* User info header */
            <Box key="user-info" sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {user.displayName || "User"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
            </Box>,
            <Divider key="user-divider" />,

            /* Theme submenu */
            <MenuItem key="theme" onClick={() => handleSubmenuOpen("theme")}>
              <ListItemIcon>
                <PaletteIcon />
              </ListItemIcon>
              <ListItemText
                primary={tTheme("label")}
                secondary={getThemeLabel(mode)}
              />
              <ChevronRightIcon />
            </MenuItem>,

            /* Language submenu */
            <MenuItem
              key="language"
              onClick={() => handleSubmenuOpen("language")}
            >
              <ListItemIcon>
                <LanguageIcon />
              </ListItemIcon>
              <ListItemText
                primary={tTheme("language")}
                secondary={getCurrentLanguageLabel()}
              />
              <ChevronRightIcon />
            </MenuItem>,

            /* Settings submenu */
            <MenuItem
              key="settings"
              onClick={() => handleSubmenuOpen("settings")}
            >
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary={tSettings("title")} />
              <ChevronRightIcon />
            </MenuItem>,

            <Divider key="logout-divider" />,

            /* Logout */
            <MenuItem
              key="logout"
              onClick={() => {
                logout();
                navigation.navigateTo("/");
                handleMenuCloseWrapper();
              }}
            >
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary={t("logout")} />
            </MenuItem>,
          ]}

          {/* Theme submenu */}
          {activeSubmenu === "theme" && [
            <MenuItem key="theme-back" onClick={handleSubmenuBack}>
              <ListItemIcon>
                <ArrowBackIcon />
              </ListItemIcon>
              <ListItemText primary={tTheme("label")} />
            </MenuItem>,
            <Divider key="theme-submenu-divider" />,

            <MenuItem
              key="theme-system"
              onClick={() => handleThemeChange("system")}
            >
              <ListItemIcon>{mode === "system" && <CheckIcon />}</ListItemIcon>
              <ListItemText primary={tTheme("system")} />
            </MenuItem>,

            <MenuItem
              key="theme-light"
              onClick={() => handleThemeChange("light")}
            >
              <ListItemIcon>{mode === "light" && <CheckIcon />}</ListItemIcon>
              <ListItemText primary={tTheme("light")} />
            </MenuItem>,

            <MenuItem
              key="theme-dark"
              onClick={() => handleThemeChange("dark")}
            >
              <ListItemIcon>{mode === "dark" && <CheckIcon />}</ListItemIcon>
              <ListItemText primary={tTheme("dark")} />
            </MenuItem>,
          ]}

          {/* Language submenu */}
          {activeSubmenu === "language" && [
            <MenuItem key="language-back" onClick={handleSubmenuBack}>
              <ListItemIcon>
                <ArrowBackIcon />
              </ListItemIcon>
              <ListItemText primary={tTheme("language")} />
            </MenuItem>,
            <Divider key="language-submenu-divider" />,

            ...LOCALS.map((locale) => (
              <MenuItem
                key={locale.code}
                onClick={() => handleLanguageChange(locale.code)}
              >
                <ListItemIcon>
                  {currentLocale === locale.code && <CheckIcon />}
                </ListItemIcon>
                <ListItemText primary={locale.label} />
              </MenuItem>
            )),
          ]}

          {/* Settings submenu */}
          {activeSubmenu === "settings" && [
            <MenuItem key="settings-back" onClick={handleSubmenuBack}>
              <ListItemIcon>
                <ArrowBackIcon />
              </ListItemIcon>
              <ListItemText primary={tSettings("title")} />
            </MenuItem>,
            <Divider key="language-submenu-divider" />,
            <MenuItem key="auto-play">
              <FormControlLabel
                control={
                  <Switch
                    checked={autoPlay}
                    onChange={(e) => {
                      changeSetting(AUTO_PLAY_KEY, e.target.checked);
                    }}
                    color="primary"
                  />
                }
                label={tSettings("autoPlay", { default: "Auto Play" })}
              />
            </MenuItem>,

            <MenuItem key="jump-to-last-watch">
              <FormControlLabel
                control={
                  <Switch
                    checked={jumpToLastWatchEnable}
                    onChange={(e) => {
                      changeSetting(
                        JUMP_TO_LAST_WATCH_ENABLE_KEY,
                        e.target.checked
                      );
                    }}
                    color="primary"
                  />
                }
                label={tSettings("jumpToLastWatchEnable", {
                  default: "Auto Jump to Last Watched",
                })}
              />
            </MenuItem>,
          ]}
        </Menu>
      </Box>
    );
  }
  // Not logged in
  if (compact) {
    // Show theme-dependent SVG when drawer is closed (compact mode)
    return <NeutralOrDarkProfileSVG size={32} />;
  }
  return <GoogleSignInButton onClick={loginWithGoogle} />;
}
